import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type TimeframeOption = "1d" | "7d" | "30d" | "all";

const STALE_THRESHOLD_MS = 60 * 1000; // 1 minute

export interface UserActivity {
  user_id: string | null;
  user_name: string;
  user_email: string;
  total_page_views: number;
  unique_pages_viewed: number;
  pages_list: string[];
  total_clicks: number;
  buttons_clicked: string[];
  last_active_at: string;
  first_seen_at: string;
  session_count: number;
}

const getStartDate = (timeframe: TimeframeOption): Date | null => {
  if (timeframe === "all") return null;
  const now = new Date();
  switch (timeframe) {
    case "1d":
      return new Date(now.getTime() - 24 * 60 * 60 * 1000);
    case "7d":
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case "30d":
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    default:
      return null;
  }
};

export const useAdminUserActivity = (timeframe: TimeframeOption = "7d") => {
  const startDate = getStartDate(timeframe);
  const hasTriggeredRefresh = useRef(false);
  const lastFetchedAtRef = useRef<Date | null>(null);

  const query = useQuery({
    queryKey: ["admin-user-activity", timeframe],
    queryFn: async () => {
      // Fetch all interactions with optional timeframe filter
      let interactionsQuery = supabase
        .from("user_interactions")
        .select("*")
        .order("created_at", { ascending: false });

      if (startDate) {
        interactionsQuery = interactionsQuery.gte("created_at", startDate.toISOString());
      }

      const { data: interactions, error } = await interactionsQuery;

      if (error) throw error;

      // Fetch all profiles for user info
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email");

      const profilesMap = new Map(
        profiles?.map((p) => [p.id, p]) || []
      );

      // Aggregate interactions by user_id (or session_id for anonymous users)
      const userActivityMap = new Map<string, {
        user_id: string | null;
        user_name: string;
        user_email: string;
        page_views: Set<string>;
        total_page_views: number;
        clicks: Set<string>;
        total_clicks: number;
        last_active_at: string;
        first_seen_at: string;
        sessions: Set<string>;
      }>();

      for (const interaction of interactions || []) {
        const key = interaction.user_id || `anonymous_${interaction.session_id}`;
        
        if (!userActivityMap.has(key)) {
          const profile = interaction.user_id ? profilesMap.get(interaction.user_id) : null;
          userActivityMap.set(key, {
            user_id: interaction.user_id,
            user_name: profile 
              ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || "Unnamed User"
              : "Anonymous",
            user_email: profile?.email || "-",
            page_views: new Set(),
            total_page_views: 0,
            clicks: new Set(),
            total_clicks: 0,
            last_active_at: interaction.created_at,
            first_seen_at: interaction.created_at,
            sessions: new Set(),
          });
        }

        const activity = userActivityMap.get(key)!;
        activity.sessions.add(interaction.session_id);

        // Update timestamps
        if (interaction.created_at > activity.last_active_at) {
          activity.last_active_at = interaction.created_at;
        }
        if (interaction.created_at < activity.first_seen_at) {
          activity.first_seen_at = interaction.created_at;
        }

        // Track based on interaction type
        if (interaction.interaction_type === "page_view") {
          // Path is now directly in target field
          const path = interaction.target || "unknown";
          activity.page_views.add(path);
          activity.total_page_views++;
        } else if (interaction.interaction_type === "click" || interaction.interaction_type === "navigation") {
          activity.clicks.add(interaction.target);
          activity.total_clicks++;
        }
      }

      // Convert to array
      const userActivities: UserActivity[] = Array.from(userActivityMap.values()).map((activity) => ({
        user_id: activity.user_id,
        user_name: activity.user_name,
        user_email: activity.user_email,
        total_page_views: activity.total_page_views,
        unique_pages_viewed: activity.page_views.size,
        pages_list: Array.from(activity.page_views).slice(0, 10),
        total_clicks: activity.total_clicks,
        buttons_clicked: Array.from(activity.clicks).slice(0, 10),
        last_active_at: activity.last_active_at,
        first_seen_at: activity.first_seen_at,
        session_count: activity.sessions.size,
      }));

      // Sort by last active
      userActivities.sort((a, b) => 
        new Date(b.last_active_at).getTime() - new Date(a.last_active_at).getTime()
      );

      lastFetchedAtRef.current = new Date();

      return userActivities;
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_THRESHOLD_MS,
  });

  // Auto-refresh if data is stale on mount
  useEffect(() => {
    if (query.isLoading || hasTriggeredRefresh.current) return;
    
    const lastFetched = lastFetchedAtRef.current;
    if (!lastFetched) return;
    
    const isStale = Date.now() - lastFetched.getTime() > STALE_THRESHOLD_MS;
    if (isStale) {
      hasTriggeredRefresh.current = true;
      query.refetch();
    }
  }, [query.isLoading, query]);

  // Reset refresh flag on timeframe change
  useEffect(() => {
    hasTriggeredRefresh.current = false;
  }, [timeframe]);

  return {
    ...query,
    lastFetchedAt: lastFetchedAtRef.current,
    isRefreshing: query.isFetching && !query.isLoading,
  };
};
